/**
 * WebAuthn browser helpers (per integration playbook) — converts between
 * base64url JSON options from the backend and the ArrayBuffer-based
 * navigator.credentials API. No fingerprint data ever leaves the device;
 * only signed assertions are transmitted.
 */
const fromB64 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

const toB64 = (b) => {
  if (!b) return undefined;
  return btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export function webauthnSupported() {
  return window.isSecureContext && !!window.PublicKeyCredential;
}

export async function webauthnCreate(options) {
  options.challenge = fromB64(options.challenge);
  options.user.id = fromB64(options.user.id);
  options.excludeCredentials = (options.excludeCredentials ?? []).map((x) => ({
    ...x, id: fromB64(x.id),
  }));
  const c = await navigator.credentials.create({ publicKey: options });
  const r = c.response;
  return {
    id: c.id, rawId: toB64(c.rawId), type: c.type,
    response: {
      clientDataJSON: toB64(r.clientDataJSON),
      attestationObject: toB64(r.attestationObject),
      transports: r.getTransports?.() ?? [],
    },
    clientExtensionResults: c.getClientExtensionResults(),
  };
}

export async function webauthnGet(options) {
  options.challenge = fromB64(options.challenge);
  delete options.allowCredentials; // discoverable / usernameless flow
  const c = await navigator.credentials.get({ publicKey: options });
  const r = c.response;
  return {
    id: c.id, rawId: toB64(c.rawId), type: c.type,
    response: {
      clientDataJSON: toB64(r.clientDataJSON),
      authenticatorData: toB64(r.authenticatorData),
      signature: toB64(r.signature),
      userHandle: toB64(r.userHandle),
    },
    clientExtensionResults: c.getClientExtensionResults(),
  };
}
